import React, { useState, useEffect } from 'react';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useAuth } from './hooks/useAuth';
import { AlertDialog } from './components/common';
import { ErrorModal } from './components/Modal';
import { PRIMARY_COLOR, FONT_FAMILY, FONT_SIZE_BASE, DEFAULT_BORDER_RADIUS, DEFAULT_BOX_SHADOW, AI_API_URL, TRANSCRIPT_API_URL } from './constants';
import { useNavigate, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { pageview, checkDuplicateForShareExtension, checkGlobalHistoryForShareExtension, copyGlobalHistoryForShareExtension } from './utils';
import LandingPage from './pages/LandingPage';
import NaverCallback from './pages/NaverCallback';
import GoogleCallback from './pages/GoogleCallback';
import KakaoCallback from './pages/KakaoCallback';
import AnalyzePage from './pages/AnalyzePage';
import EmailActionPage from './pages/EmailActionPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import LoadingSpinner from './components/common/LoadingSpinner';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { auth } from './services/firebase';
import { signInWithCustomToken } from 'firebase/auth';
import api from './services/api';
import i18n from './i18n';
import { useTranslation } from 'react-i18next';


// 에러 바운더리 컴포넌트
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Route error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // 에러 발생 시 홈으로 리다이렉트
      window.location.href = '/';
      return null;
    }

    return this.props.children;
  }
}

const theme = createTheme({
  palette: {
    primary: { main: PRIMARY_COLOR },
  },
  typography: {
    fontFamily: FONT_FAMILY,
    fontSize: FONT_SIZE_BASE,
  },
  shape: {
    borderRadius: DEFAULT_BORDER_RADIUS,
  },
  shadows: [
    'none',
    DEFAULT_BOX_SHADOW,
    ...Array(23).fill('none'), // 나머지 그림자 기본값
  ],
});

// API 연결 테스트 컴포넌트
const APIConnectionTest = () => {
  React.useEffect(() => {
    const testAPIConnection = async () => {
      try {
        console.log('🧪 Testing API connections...');
        
        // AI API 테스트
        const aiResponse = await fetch(`${AI_API_URL}/`, { 
          method: 'GET',
          timeout: 5000 
        });
        console.log('✅ AI API Connection:', aiResponse.status, aiResponse.statusText);
        
        // Transcript API 테스트  
        const transcriptResponse = await fetch(`${TRANSCRIPT_API_URL}/`, {
          method: 'GET', 
          timeout: 5000
        });
        console.log('✅ Transcript API Connection:', transcriptResponse.status, transcriptResponse.statusText);
        
      } catch (error) {
        console.error('❌ API Connection Test Failed:', error);
      }
    };
    
    // 1초 후 테스트 실행 (앱 초기화 완료 후)
    setTimeout(testAPIConnection, 1000);
  }, []);
  
  return null; // UI 컴포넌트가 아니므로 렌더링하지 않음
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Share Extension 로딩 상태 관리
  const [shareExtensionLoading, setShareExtensionLoading] = useState(false);
  
  // Share Extension용 에러 모달 상태 (기존 ErrorModal 사용)
  const [shareErrorModalOpen, setShareErrorModalOpen] = useState(false);
  const [shareErrorType, setShareErrorType] = useState('');
  const [shareErrorMessage, setShareErrorMessage] = useState('');

  const {
    handleNativeLogin,
    alertDialog,
    closeAlert,
    isLoggedIn,
    userId,
    setAuthLoading // 🔧 로딩 스피너 제어를 위해 추가
  } = useAuth();
  
  // Share Extension용 에러 모달 표시 함수 (기존 ErrorModal 사용)
  const showShareErrorModal = (errorType, customMessage = '') => {
    setShareErrorType(errorType);
    setShareErrorMessage(customMessage);
    setShareErrorModalOpen(true);
  };
  
  // Share Extension용 에러 모달 닫기 함수
  const closeShareErrorModal = () => {
    setShareErrorModalOpen(false);
    setShareErrorType('');
    setShareErrorMessage('');
  };

  // FCM 토큰 등록 상태 추적 제거 (근본원인 해결로 불필요)

  // Capacitor App URL 리스너
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const listener = CapacitorApp.addListener('appUrlOpen', async (data) => {
      console.log('✅ App opened with URL:', data.url);
      const url = new URL(data.url);
      
      // Android ShareActivity에서 온 분석 요청 처리
      if (data.url.includes('ai.tubedigest.app://share-analysis')) {
        console.log('🤖 [Android ShareActivity] URL 감지, 파라미터 추출하여 sessionStorage에 저장');
        
        try {
          const url = new URL(data.url);
          const videoURL = url.searchParams.get('url');
          const language = url.searchParams.get('language');
          
          if (videoURL && language) {
            // Android 데이터를 sessionStorage에 저장 (iOS pendingAnalysis 형식에 맞춤)
            const androidShareData = {
              videoURL: decodeURIComponent(videoURL), // Android는 videoURL 사용
              language: language,
              timestamp: Date.now(),
              source: 'android-share-activity'
            };
            
            sessionStorage.setItem('pendingAnalysis', JSON.stringify(androidShareData));
            console.log('✅ [Android ShareActivity] 데이터 sessionStorage에 저장 완료:', androidShareData);
          } else {
            console.error('❌ [Android ShareActivity] 필수 파라미터 누락:', { videoURL, language });
          }
        } catch (error) {
          console.error('❌ [Android ShareActivity] URL 파싱 실패:', error);
        }
        
        return;
      }
      
      // 기존 로그인 처리
      const token = url.searchParams.get('token');
      const error = url.searchParams.get('message');

      await Browser.close();
      
      // 🔧 딥링크 콜백 처리 시 로딩 스피너 종료
      if (setAuthLoading) {
        setAuthLoading(false);
        console.log('🔄 [App] 딥링크 콜백 로딩 스피너 종료');
      }
      
      if (error) {
        console.error('❌ Login failed from URL:', error);
        return;
      }
      
      // 카카오 OAuth 콜백 처리
      if (data.url.includes('tubedigest://auth/kakao')) {
        console.log('🔐 [App] 카카오 OAuth 딥링크 콜백 처리');
        
        const provider = url.searchParams.get('provider');
        const merged = url.searchParams.get('merged') === 'true';
        const email = url.searchParams.get('email');
        
        if (token) {
          const user = await handleNativeLogin(token);
          if (user) {
            if (merged) {
              // 계정 연동 성공 시 메시지 표시
              console.log('✅ 기존 계정에 카카오 OAuth가 연동되었습니다!');
              // AlertDialog로 연동 성공 메시지 표시하거나 홈으로 이동
              navigate('/');
            } else {
              // 일반 로그인 성공 시 홈으로 이동
              navigate('/');
            }
          }
        }
        return;
      }
      
      if (data.url.includes('/login/success') && token) {
        const user = await handleNativeLogin(token);
        if (user) {
          const historyId = url.searchParams.get('historyId');
          if (historyId) {
            navigate(`/analyze/${historyId}`);
          } else {
            navigate('/');
          }
        }
      }
    });

    return () => {
      listener.remove();
    };
  }, [navigate, handleNativeLogin, setAuthLoading]); // 🔧 setAuthLoading 의존성 추가

  // iOS Share Extension CustomEvent 리스너 (Local Notification 방식)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    console.log('🔧 [Share Extension] CustomEvent 리스너 등록');

    const handleShareAnalysisEvent = async (event) => {
      console.log('🚀 [Share Extension] 단순화된 CustomEvent 수신:', event.detail);
      
      const shareData = event.detail;
      
      // 단순화된 검증 - URL과 언어만 필요
      if (!shareData || !shareData.videoURL || !shareData.language) {
        console.error('❌ [Share Extension] 필수 데이터 누락:', shareData);
        return;
      }
      
      try {
        // 🔄 Share Extension 로딩 시작
        setShareExtensionLoading(true);
        
        console.log('📋 [Share Extension] 단순화된 처리 데이터:', {
          videoURL: shareData.videoURL,
          language: shareData.language,
          timestamp: shareData.timestamp
        });
        
        // 🌐 Share Extension에서 선택한 언어로 UI 언어 변경
        if (shareData.language && shareData.language !== i18n.language) {
          console.log(`🌐 [Share Extension] UI 언어 변경: ${i18n.language} → ${shareData.language}`);
          await i18n.changeLanguage(shareData.language);
          
          // 언어 설정을 localStorage에 저장 (다음 앱 실행 시에도 유지)
          localStorage.setItem('i18nextLng', shareData.language);
          console.log(`💾 [Share Extension] 언어 설정 저장 완료: ${shareData.language}`);
        } else {
          console.log(`📝 [Share Extension] UI 언어 유지: ${i18n.language} (선택한 언어와 동일)`);
        }
        
        // 사용자 인증 확인
        if (!userId) {
          console.warn('⚠️ [Share Extension] 사용자 미인증 - 로그인 후 분석 진행');
          // 로그인 후 분석을 위해 데이터 임시 저장
          sessionStorage.setItem('pendingAnalysis', JSON.stringify({
            youtubeUrl: shareData.videoURL,
            language: shareData.language,
            timestamp: shareData.timestamp,
            showLoginModal: true  // 로그인 모달 자동 띄우기 플래그
          }));
          setShareExtensionLoading(false);
          navigate('/');
          return;
        }
        
        // 🚀 직접입력과 동일한 공통 함수 사용
        console.log('🚀 [Share Extension] 공통 함수 handleVideoAnalysis 호출');
        
        // 입력 URL 설정 (기존 직접입력 방식과 동일)
        const { useVideo } = await import('./hooks/useVideo');
        const { useAnalysisStarter } = await import('./hooks/useVideo/useAnalysisStarter');
        
        // 임시로 inputUrl 상태 설정
        const tempInputUrl = shareData.videoURL;
        
        // 공통 분석 함수 호출을 위한 필요한 상태들 설정
        // 이는 실제 컴포넌트 내에서 사용되는 상태들의 임시 버전
        let tempLoading = false;
        let tempAnalyzeLoading = false;
        const tempHistory = [];
        
        // 공통 함수 호출을 위한 설정
        const tempSetInputUrl = (url) => { /* 임시 함수 */ };
        const tempSetInputTitle = (title) => { /* 임시 함수 */ };
        const tempSetLoading = (loading) => { tempLoading = loading; };
        const tempSetAnalyzeLoading = (loading) => { tempAnalyzeLoading = loading; };
        const tempShowErrorModal = (errorType, customMessage, retryCallback) => {
          console.error(`❌ [Share Extension] 에러 발생: ${errorType}`, customMessage);
          showShareErrorModal(errorType, customMessage);
          setShareExtensionLoading(false);
          navigate('/');
        };
        const tempParseISO8601Duration = (duration) => { /* 임시 함수 */ };
        const tempSetVideoDuration = (duration) => { /* 임시 함수 */ };
        
        // 실제 분석 시작 - 직접입력과 동일한 로직 사용
        console.log('🔍 [Share Extension] 직접입력 방식으로 분석 시작');
        
        // 1. URL 검증 및 Video ID 추출
        const { extractVideoId, checkIsYouTubeShorts } = await import('./utils');
        const { ERROR_TYPES } = await import('./components/Modal/ErrorModal');
        
        if (checkIsYouTubeShorts(tempInputUrl)) {
          tempShowErrorModal(ERROR_TYPES.YOUTUBE_SHORTS);
          return;
        }
        
        const extractedVideoId = extractVideoId(tempInputUrl);
        if (!extractedVideoId) {
          tempShowErrorModal(ERROR_TYPES.INVALID_URL);
          return;
        }
        
        // 2. Share Extension에서도 사전체크 수행 (네이티브 직접입력과 동일)
        console.log('🔍 [Share Extension] 사전체크 시작 - 자막 존재 여부 확인');
        
        try {
          const { performPreAnalysisCheck } = await import('./utils/preAnalysisCheck');
          const preCheckResult = await performPreAnalysisCheck(extractedVideoId, tempInputUrl);
          console.log('✅ [Share Extension] 사전체크 완료:', preCheckResult);
        } catch (preCheckError) {
          console.error('❌ [Share Extension] 사전체크 실패:', preCheckError);
          // 사전체크 실패 시 에러 표시하고 중단
          tempShowErrorModal('INVALID_VIDEO', preCheckError.message);
          return;
        }
        
        // 3. 영상 정보 가져오기 (플랫폼별 처리)
        console.log(`🔍 [Share Extension] 영상 정보 가져오기 (${Capacitor.getPlatform()} 플랫폼)`);
        const { default: api } = await import('./services/api');
        let videoTitle = '';
        
        try {
          // 플랫폼 헤더를 동적으로 설정
          const videoInfoResponse = await api.get(`/api/video-info/${extractedVideoId}`, {
            headers: {
              'X-Platform': Capacitor.getPlatform(),
              'X-Client-Type': 'native',
              'X-Share-Extension': 'true'
            }
          });
          if (videoInfoResponse.data && videoInfoResponse.data.data) {
            videoTitle = videoInfoResponse.data.data.title || '';
          }
        } catch (error) {
          console.warn('⚠️ [Share Extension] 영상 정보 가져오기 실패:', error);
          // 실패해도 계속 진행
        }

        // 📱 Share Extension: 클라이언트 사이드 자막 추출 시도
        console.log(`📱 [Share Extension] ${Capacitor.getPlatform()} 클라이언트 사이드 자막 추출 시도`);
        let transcript = '';
        let transcriptSource = '';
        let extractionMethod = '';
        let clientSideExtraction = false;
        let sourceLanguage = null; // 🔥 원본 언어 정보 추가
        
        try {
          // 네이티브 자막 추출 시도
          const { getTranscript } = await import('./services/youtube');
          const transcriptResult = await getTranscript(tempInputUrl, userId, null);
          
          if (transcriptResult && transcriptResult.transcript) {
            transcript = transcriptResult.transcript;
            transcriptSource = transcriptResult.source; // 'native' or 'server'
            extractionMethod = transcriptResult.method;
            clientSideExtraction = transcriptResult.source === 'native';
            sourceLanguage = transcriptResult.sourceLanguage; // 🔥 원본 언어 정보 추출
            
            console.log('✅ [Share Extension] 클라이언트 사이드 자막 추출 성공:', {
              transcriptLength: transcript.length,
              source: transcriptSource,
              method: extractionMethod,
              clientSideExtraction: clientSideExtraction,
              sourceLanguage: sourceLanguage
            });
          }
        } catch (transcriptError) {
          console.warn('⚠️ [Share Extension] 클라이언트 사이드 자막 추출 실패, 백엔드에서 처리:', transcriptError);
          // 실패 시 백엔드에서 처리하도록 빈 값으로 설정
          transcript = '';
          transcriptSource = 'extraction_failed';
          extractionMethod = 'extraction_failed';
          clientSideExtraction = false;
          sourceLanguage = null;
        }
        
        // 4. Share Extension 중복체크 프로세스 (일반 분석과 동일)
        console.log('🔍 [Share Extension] 중복체크 프로세스 시작');
        
        // 4-1. 로컬 히스토리 중복체크 (클라이언트 사이드)
        console.log('🔍 [Share Extension] 1단계: 로컬 히스토리 중복체크');
        // 현재 Share Extension에서는 history 데이터가 없으므로 스킵
        
        // 4-2. 개인 히스토리 중복체크 (API)
        console.log('🔍 [Share Extension] 2단계: 개인 히스토리 중복체크');
        try {
          const { default: api } = await import('./services/api');
          const currentPlatform = Capacitor.getPlatform();
          const personalDuplicateResponse = await api.get(`/api/streaming/check-personal-history/${extractedVideoId}?language=${shareData.language}`, {
            headers: {
              'user-id': userId,
              'X-Platform': currentPlatform,
              'X-Share-Extension': 'true'
            }
          });
          
          if (personalDuplicateResponse.data && personalDuplicateResponse.data.exists) {
            console.log('✅ [Share Extension] 개인 히스토리에서 중복 발견:', personalDuplicateResponse.data);
            
            // 중복된 히스토리로 바로 이동
            const existingHistoryId = personalDuplicateResponse.data.historyId;
            setShareExtensionLoading(false);
            navigate(`/analyze/${existingHistoryId}`);
            return;
          }
        } catch (personalCheckError) {
          console.warn('⚠️ [Share Extension] 개인 히스토리 중복체크 실패:', personalCheckError);
          // 실패해도 계속 진행
        }
        
        // 4-3. 광역 히스토리 중복체크 및 복사
        console.log('🔍 [Share Extension] 3단계: 광역 히스토리 중복체크 및 복사');
        try {
          const { default: api } = await import('./services/api');
          const currentPlatform = Capacitor.getPlatform();
          const globalHistoryResponse = await api.get(`/api/streaming/check-global-history/${extractedVideoId}?language=${shareData.language}`, {
            headers: {
              'user-id': userId,
              'X-Platform': currentPlatform,
              'X-Share-Extension': 'true'
            }
          });
          
          if (globalHistoryResponse.data && globalHistoryResponse.data.exists) {
            console.log('✅ [Share Extension] 광역 히스토리에서 중복 발견, 복사 시도:', globalHistoryResponse.data);
            
            // 광역 히스토리 복사 시도
            const copyResponse = await api.post(`/api/streaming/copy-from-global-history/${extractedVideoId}?language=${shareData.language}`, {}, {
              headers: {
                'user-id': userId,
                'X-Platform': currentPlatform,
                'X-Share-Extension': 'true'
              }
            });
            
            if (copyResponse.data && copyResponse.data.success) {
              console.log('✅ [Share Extension] 광역 히스토리 복사 성공:', copyResponse.data);
              
              const copiedHistoryId = copyResponse.data.historyId;
              setShareExtensionLoading(false);
              navigate(`/analyze/${copiedHistoryId}`);
              return;
            }
          }
        } catch (globalCheckError) {
          console.warn('⚠️ [Share Extension] 광역 히스토리 중복체크 실패:', globalCheckError);
          // 실패해도 계속 진행
        }
        
        // 5. 새로운 히스토리 생성 (중복체크 통과 후)
        console.log('🚀 [Share Extension] 중복체크 통과 - 새로운 히스토리 생성');
        
        const languageNames = {
          'ko': '한국어',
          'en': 'English', 
          'zh': '中文',
          'ja': '日本語',
          'es': 'Español',
          'fr': 'Français'
        };
        
        const { db } = await import('./services/firebase');
        const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
        
        const newVideo = {
          url: tempInputUrl,
          videoId: extractedVideoId,
          title: videoTitle,
          youtubeTitle: videoTitle,
          customTitle: videoTitle,
          summary: "",
          chunk_summaries: [],
          chapters: [],
          transcript: transcript, // 클라이언트에서 추출된 자막
          subtitle: transcript, // 호환성을 위해 동일하게 설정
          status: 'analyzing',
          userId: userId,
          lastUpdated: serverTimestamp(),
          createdAt: serverTimestamp(),
          qa: [],
          questions: [],
          projectId: null,
          language: shareData.language,
          languageName: languageNames[shareData.language] || 'Unknown',
          // 플랫폼별 정보 설정
          platform: Capacitor.getPlatform(),
          clientSideExtraction: clientSideExtraction,
          transcriptSource: transcriptSource,
          extractionMethod: extractionMethod,
          source: 'share-extension',
          shareExtension: true,
          sourceLanguage: sourceLanguage // 🔥 원본 언어 정보 저장
        };
        
        const docRef = await addDoc(collection(db, "users", userId, "history"), newVideo);
        const historyId = docRef.id;
        
        console.log(`✅ [Share Extension] ${Capacitor.getPlatform()} 새로운 히스토리 생성 성공:`, historyId);
        
        // 분석 페이지로 이동
        setShareExtensionLoading(false);
        navigate(`/analyze/${historyId}`);
        
      } catch (error) {
        console.error('❌ [Share Extension] 분석 실패:', error);
        setShareExtensionLoading(false);
        navigate('/');
      }
    };

    // CustomEvent 리스너 등록
    window.addEventListener('tubeDigestShareAnalysis', handleShareAnalysisEvent);

    // 백업 데이터 확인 (앱 시작 시)
    const checkBackupData = () => {
      console.log('🔍 [Share Extension] sessionStorage 백업 데이터 확인');
      
      // iOS에서도 백업 데이터 처리 (백그라운드 재연결 지원)
      if (Capacitor.getPlatform() === 'ios') {
        console.log('📱 [Share Extension] iOS 플랫폼 - 백업 데이터 확인');
      }
      
      const backupData = sessionStorage.getItem('pendingShareAnalysis');
      
      if (backupData) {
        try {
          const shareData = JSON.parse(backupData);
          console.log('📋 [Share Extension] 백업 데이터 발견:', shareData);
          
          // 백업 데이터 삭제
          sessionStorage.removeItem('pendingShareAnalysis');
          
          // 🌐 백업 데이터의 분석 언어로 UI 언어 변경
          if (shareData.language && shareData.language !== i18n.language) {
            console.log(`🌐 [백업 데이터] UI 언어 변경: ${i18n.language} → ${shareData.language}`);
            i18n.changeLanguage(shareData.language).then(() => {
              // 언어 설정을 localStorage에 저장 (다음 앱 실행 시에도 유지)
              localStorage.setItem('i18nextLng', shareData.language);
              console.log(`💾 [백업 데이터] 언어 설정 저장 완료: ${shareData.language}`);
            });
          } else {
            console.log(`📝 [백업 데이터] UI 언어 유지: ${i18n.language} (선택한 언어와 동일)`);
          }
          
          const event = new CustomEvent('tubeDigestShareAnalysis', {
            detail: shareData
          });
          
          // 약간의 지연 후 이벤트 발생 (앱이 완전히 로드되도록)
          setTimeout(() => {
            console.log('🚀 [Share Extension] 백업 데이터로 이벤트 발생');
            window.dispatchEvent(event);
          }, 100);
          
        } catch (error) {
          console.error('❌ [Share Extension] 백업 데이터 파싱 실패:', error);
          sessionStorage.removeItem('pendingShareAnalysis');
        }
      }
    };
    
    // 앱 시작 시 백업 데이터 확인
    checkBackupData();

    return () => {
      console.log('🔧 [Share Extension] CustomEvent 리스너 해제');
      window.removeEventListener('tubeDigestShareAnalysis', handleShareAnalysisEvent);
    };
  }, [userId, navigate]); // userId와 navigate 의존성 추가

  // 📱 웹서치에서 찾은 검증된 Capacitor Push Notifications 표준 구현 (플랫폼별 차별화)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // 중복 방지 로직 제거 (근본원인 해결로 불필요)

    const platform = Capacitor.getPlatform();
    console.log(`📱 [Push] ${platform} 플랫폼별 차별화된 Push Notifications 설정 시작`);

    let listenersRegistered = false;

    const setupPushNotifications = async () => {
      try {
        // 리스너 중복 방지 로직 제거 (근본원인 해결로 불필요)

        console.log(`📱 [Push] ${platform} 플랫폼별 리스너 해제`);
        
        // 기존 리스너들 정리 (중복 방지)
        if (window.pushNotificationListeners) {
          window.pushNotificationListeners.forEach(listener => {
            if (listener && typeof listener.remove === 'function') {
              listener.remove();
            }
          });
          window.pushNotificationListeners = [];
        } else {
          window.pushNotificationListeners = [];
        }

        // 웹서치에서 찾은 표준 방식: @capacitor/push-notifications 사용
        const { PushNotifications } = await import('@capacitor/push-notifications');
        
        console.log(`📱 [Push] @capacitor/push-notifications 플러그인 로드 성공 (${platform})`);

        // 🎯 플랫폼별 차별화된 이벤트 리스너들 등록
        
        // 1. 토큰 등록 성공 리스너 (플랫폼별 최적화)
        const registrationListener = await PushNotifications.addListener('registration', async (token) => {
          console.log(`📱 [Push] ${platform} 토큰 등록 성공:`, token.value);
          
          if (!token.value) {
            console.error(`❌ [Push] ${platform} 토큰이 없습니다`);
            return;
          }
          
          if (!userId) {
            console.log(`⚠️ [Push] ${platform} 사용자 미인증 - 토큰 저장 보류`);
            // 로그인 후 처리를 위해 플랫폼 정보와 함께 임시 저장
            sessionStorage.setItem('pendingFCMToken', JSON.stringify({ 
              token: token.value, 
              platform: platform,
              capacitorHttpEnabled: platform === 'ios', // iOS는 CapacitorHttp 활성화
              timestamp: Date.now()
            }));
            return;
          }
          
          // 플랫폼별 토큰 등록 처리
          await registerFCMTokenWithPlatformOptimization(token.value, platform);
        });
        window.pushNotificationListeners.push(registrationListener);

        // 2. 토큰 등록 실패 리스너 (플랫폼별 에러 처리)
        const registrationErrorListener = await PushNotifications.addListener('registrationError', (error) => {
          console.error(`❌ [Push] ${platform} 토큰 등록 실패:`, error.error);
          
          // 플랫폼별 에러 처리
          if (platform === 'ios') {
            console.error('🍎 [Push] iOS FCM 등록 실패 - APNS 설정 확인 필요');
          } else if (platform === 'android') {
            console.error('🤖 [Push] Android FCM 등록 실패 - Firebase 설정 확인 필요');
          }
        });
        window.pushNotificationListeners.push(registrationErrorListener);

        // 3. 푸시 알림 수신 리스너 (플랫폼별 처리)
        const notificationReceivedListener = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log(`📊 [Push] ${platform} 알림 수신:`, notification);
          
          // 플랫폼별 알림 처리 최적화
          handleNotificationReceived(notification, platform);
        });
        window.pushNotificationListeners.push(notificationReceivedListener);

        // 4. 푸시 알림 클릭 리스너 (플랫폼별 네비게이션)
        const notificationActionListener = await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log(`👆 [Push] ${platform} 알림 클릭됨:`, notification);
          
          // 플랫폼별 클릭 액션 처리
          handleNotificationAction(notification, platform);
        });
        window.pushNotificationListeners.push(notificationActionListener);

        // 🔧 리스너 등록 완료 플래그 설정
        listenersRegistered = true;

        // 🚀 플랫폼별 최적화된 권한 요청 및 등록
        console.log(`📱 [Push] ${platform} 플랫폼별 권한 확인 중...`);
        let permStatus = await PushNotifications.checkPermissions();
        
        if (permStatus.receive === 'prompt') {
          console.log(`📱 [Push] ${platform} 권한 요청 중...`);
          permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive !== 'granted') {
          console.error(`❌ [Push] ${platform} 사용자가 알림 권한을 거부했습니다`);
          
          // 플랫폼별 권한 거부 안내
          if (platform === 'ios') {
            console.log('🍎 [Push] iOS 설정 > TubeDigest > 알림에서 권한을 활성화해주세요');
          } else if (platform === 'android') {
            console.log('🤖 [Push] Android 설정 > 앱 > TubeDigest > 알림에서 권한을 활성화해주세요');
          }
          return;
        }

        console.log(`✅ [Push] ${platform} 알림 권한 허용됨, 등록 시작...`);
        
        // 플랫폼별 등록 전략
        if (platform === 'ios') {
          // iOS: CapacitorHttp enabled 환경에서 최적화
          console.log('🍎 [Push] iOS 최적화: CapacitorHttp + Long Polling 환경');
          await PushNotifications.register();
        } else if (platform === 'android') {
          // Android: 기본 HTTP 환경에서 최적화
          console.log('🤖 [Push] Android 최적화: 기본 HTTP 환경');
          await PushNotifications.register();
        }
        
        console.log(`✅ [Push] ${platform} 플랫폼별 최적화된 Push Notifications 설정 완료`);

        // Cleanup 함수 반환
        return () => {
          console.log(`🔧 [Push] ${platform} Push Notifications 리스너 정리`);
          if (window.pushNotificationListeners) {
            window.pushNotificationListeners.forEach(listener => {
              if (listener && typeof listener.remove === 'function') {
                listener.remove();
              }
            });
            window.pushNotificationListeners = [];
          }
          listenersRegistered = false;
        };

      } catch (error) {
        console.error(`❌ [Push] ${platform} Push Notifications 설정 실패:`, error);
        
        // 플랫폼별 설정 실패 처리
        if (platform === 'ios') {
          console.error('🍎 [Push] iOS 설정 실패 - Capacitor 설정 확인 필요');
        } else if (platform === 'android') {
          console.error('🤖 [Push] Android 설정 실패 - Firebase 설정 확인 필요');
        }
      }
    };

    setupPushNotifications();

  }, [userId]); // 의존성 배열 단순화

  // 🔧 플랫폼별 FCM 토큰 등록 최적화 함수
  const registerFCMTokenWithPlatformOptimization = async (token, platform) => {
    try {
      console.log(`📤 [Push] ${platform} 플랫폼별 최적화된 토큰 등록 시작`);
      
      // 🚨 중복 등록 방지: 같은 토큰이 이미 등록된 경우 스킵
      const lastRegisteredToken = localStorage.getItem(`fcm_token_${platform}`);
      if (lastRegisteredToken === token) {
        console.log(`⏭️ [Push] ${platform} 동일 토큰 이미 등록됨 - 등록 스킵`);
        return;
      }
      
      // 🚨 SECURITY FIX: 더 안전한 디바이스 식별 방식
      const getDeviceId = () => {
        // 기존 디바이스 ID가 있는지 확인 (동일 디바이스에서 다른 사용자 로그인 시)
        let deviceId = localStorage.getItem('tubedigest_device_id');
        
        if (!deviceId) {
          // 새로운 디바이스 ID 생성 (사용자 독립적)
          const timestamp = Date.now();
          const random = Math.random().toString(36).substr(2, 9);
          deviceId = `${platform}_${timestamp}_${random}`;
          localStorage.setItem('tubedigest_device_id', deviceId);
          console.log(`🆔 [Security] 새로운 디바이스 ID 생성: ${deviceId}`);
        } else {
          console.log(`🆔 [Security] 기존 디바이스 ID 사용: ${deviceId}`);
        }
        
        return deviceId;
      };
      
      // 플랫폼별 디바이스 정보 생성 - 사용자 독립적인 device_id 사용
      const deviceInfo = {
        token: token,
        device_type: platform,
        device_id: getDeviceId(), // 🚨 사용자 독립적인 고유 디바이스 ID
        device_name: `${platform === 'ios' ? '🍎 iOS' : '🤖 Android'} Device`,
        user_id: userId, // 🚨 사용자 정보는 별도 필드로 관리
        platform_config: {
          capacitor_http_enabled: platform === 'ios',
          firebase_long_polling: platform === 'ios',
          optimized_for: platform
        }
      };
      
      console.log(`🎯 [Push] ${platform} 사용자 독립적 device_id 사용: ${deviceInfo.device_id}`);
      
      // 플랫폼별 네트워크 최적화
      let requestConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId
        },
        body: JSON.stringify(deviceInfo)
      };
      
      // 플랫폼별 API 요청 최적화
      if (platform === 'ios') {
        console.log('🍎 [Push] iOS 네트워크 최적화: Long Polling 호환 설정');
        // iOS: Capacitor HTTP 사용
        const response = await api.post('/api/v1/fcm/register-token', deviceInfo, {
          headers: { 'user-id': userId }
        });
        console.log(`✅ [Push] ${platform} 플랫폼별 최적화된 토큰 등록 성공:`, response.data);
        
      } else {
        console.log('🤖 [Push] Android 네트워크 최적화: 기본 HTTP 설정');
        // Android: 기본 fetch 사용
        const response = await fetch(`${AI_API_URL}/api/v1/fcm/register-token`, requestConfig);
        const data = await response.json();
        console.log(`✅ [Push] ${platform} 플랫폼별 최적화된 토큰 등록 성공:`, data);
      }
      
      // 🚨 등록 성공 시 토큰 저장 (중복 방지용)
      localStorage.setItem(`fcm_token_${platform}`, token);
      
      console.log(`🍎 [Push] ${platform === 'ios' ? 'iOS' : 'Android'} 푸시 알림 준비 완료 (${platform === 'ios' ? 'CapacitorHttp + Long Polling' : '기본 HTTP'})`);
      
    } catch (error) {
      console.error(`❌ [Push] ${platform} 토큰 등록 실패:`, error);
      
      // 플랫폼별 에러 처리
      if (platform === 'ios' && error.message?.includes('CapacitorHttp')) {
        console.error('🍎 [Push] iOS CapacitorHttp 오류 - 네트워크 설정 확인 필요');
      } else if (platform === 'android' && error.message?.includes('fetch')) {
        console.error('🤖 [Push] Android fetch 오류 - 네트워크 설정 확인 필요');
      }
    }
  };

  // 🔔 플랫폼별 알림 수신 처리
  const handleNotificationReceived = (notification, platform) => {
    // FCM 분석 완료 알림 처리
    if (notification.data && notification.data.type === 'analysis_complete') {
      const historyId = notification.data.historyId;
      const notificationId = notification.data.notificationId;
      console.log(`🎯 [Push] ${platform} 분석 완료 알림 - 히스토리 ID: ${historyId}, 알림 ID: ${notificationId}`);
      
      // 플랫폼별 알림 표시 최적화
      if (platform === 'ios') {
        console.log('🍎 [Push] iOS 알림 수신 - 시스템 알림 센터 표시');
      } else if (platform === 'android') {
        console.log('🤖 [Push] Android 알림 수신 - 상태바 알림 표시');
      }
      
      // 현재 경로와 알림의 경로가 다르면 이동 (이미 올바른 로직)
      if (window.location.pathname !== `/analyze/${historyId}`) {
        console.log(`🚀 [Push] ${platform} 분석 결과 페이지로 이동: /analyze/${historyId}`);
        navigate(`/analyze/${historyId}`);  // 🎯 백그라운드 재연결 로직과 동일하게 단순화
      } else {
        console.log(`📍 [Push] ${platform} 이미 해당 페이지에 있음 - navigate 생략하여 모달 유지`);
      }
    }
  };

  // 🔔 개선된 알림 읽음 처리 함수 (중복 방지, 재시도, 오프라인 지원, 즉시 로컬 반영)
  const markNotificationAsRead = async (notificationId, platform, retryCount = 0) => {
    if (!notificationId || !userId) return false;

    // 중복 호출 방지
    const readKey = `read_${notificationId}`;
    if (window[readKey]) {
      console.log(`🔄 [Push] ${platform} 알림 읽음 처리 이미 진행 중: ${notificationId}`);
      return false;
    }
    
    window[readKey] = true;

    // 🚀 즉시 로컬 상태 반영 (UX 개선) - 서버 응답 전에 즉시 UI 업데이트
    if (retryCount === 0) {
      try {
        // iOS 네이티브 알림 센터에서 즉시 반영 (개별 제거는 API 제한으로 생략)
        if (Capacitor.isNativePlatform() && platform === 'ios') {
          console.log(`🎯 [Push] ${platform} 알림 클릭 감지 - 네이티브 상태 동기화됨: ${notificationId}`);
        }
        
        // 로컬 읽음 상태 저장 (UI 즉시 반영용)
        const localReadNotifications = JSON.parse(localStorage.getItem('localReadNotifications') || '[]');
        if (!localReadNotifications.includes(notificationId)) {
          localReadNotifications.push(notificationId);
          localStorage.setItem('localReadNotifications', JSON.stringify(localReadNotifications));
          console.log(`💾 [Push] ${platform} 로컬 읽음 상태 즉시 저장: ${notificationId}`);
        }
      } catch (localError) {
        console.warn(`⚠️ [Push] ${platform} 로컬 상태 반영 실패 (서버 처리는 계속):`, localError);
      }
    }

    try {
      console.log(`📖 [Push] ${platform} 알림 읽음 처리 시작: ${notificationId} (시도 ${retryCount + 1})`);
      
      const response = await api.post('/api/v1/fcm/mark-notification-read', null, {
        params: { notification_id: notificationId },
        headers: { 'user-id': userId }
      });
      
      if (response.data.success) {
        console.log(`✅ [Push] ${platform} 알림 읽음 처리 완료, 미읽은 개수: ${response.data.unread_count}`);
        
        // 서버 성공 시 로컬 읽음 상태에서 제거 (서버와 동기화됨)
        const localReadNotifications = JSON.parse(localStorage.getItem('localReadNotifications') || '[]');
        const updatedLocal = localReadNotifications.filter(id => id !== notificationId);
        localStorage.setItem('localReadNotifications', JSON.stringify(updatedLocal));
        
        delete window[readKey]; // 성공 시 플래그 제거
        return true;
      }
      
    } catch (error) {
      console.error(`❌ [Push] ${platform} 알림 읽음 처리 실패 (시도 ${retryCount + 1}):`, error);
      
      // 네트워크 오류 시 재시도 (최대 3회)
      if (retryCount < 2 && (
        error.code === 'NETWORK_ERROR' || 
        error.message?.includes('timeout') ||
        error.message?.includes('fetch')
      )) {
        console.log(`🔄 [Push] ${platform} 네트워크 오류로 재시도 예약: ${notificationId}`);
        delete window[readKey]; // 재시도를 위해 플래그 제거
        
        setTimeout(() => {
          markNotificationAsRead(notificationId, platform, retryCount + 1);
        }, (retryCount + 1) * 2000); // 2초, 4초, 6초 간격으로 재시도
        
        return false;
      }
      
      // 오프라인 상태 감지 시 로컬 저장
      if (!navigator.onLine) {
        console.log(`📱 [Push] ${platform} 오프라인 상태 - 알림 읽음 처리 대기열에 추가: ${notificationId}`);
        const offlineQueue = JSON.parse(localStorage.getItem('offlineNotificationQueue') || '[]');
        if (!offlineQueue.includes(notificationId)) {
          offlineQueue.push(notificationId);
          localStorage.setItem('offlineNotificationQueue', JSON.stringify(offlineQueue));
        }
      }
      
      delete window[readKey]; // 실패 시에도 플래그 제거
      return false;
    }
  };

  // 🌐 온라인 복구 시 대기 중인 알림 읽음 처리
  const processOfflineNotificationQueue = async () => {
    if (!navigator.onLine || !userId) return;

    const offlineQueue = JSON.parse(localStorage.getItem('offlineNotificationQueue') || '[]');
    if (offlineQueue.length === 0) return;

    console.log(`🌐 [Push] 온라인 복구 - 대기 중인 알림 읽음 처리 시작: ${offlineQueue.length}개`);

    for (const notificationId of offlineQueue) {
      const success = await markNotificationAsRead(notificationId, 'offline-recovery');
      if (success) {
        // 성공한 알림은 대기열에서 제거
        const updatedQueue = offlineQueue.filter(id => id !== notificationId);
        localStorage.setItem('offlineNotificationQueue', JSON.stringify(updatedQueue));
      }
      
      // 각 요청 사이에 200ms 지연 (서버 부하 방지)
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    // 🧹 로컬 읽음 상태 정리 (온라인 복구 후 서버와 동기화 완료)
    localStorage.removeItem('localReadNotifications');
    console.log(`✅ [Push] 오프라인 알림 읽음 처리 완료 - 로컬 상태 정리됨`);
  };

  // 알림 클릭 처리 (백그라운드 재연결 로직과 동일하게 단순화)
  const handleNotificationAction = async (notification, platform) => {
    // 분석 완료 알림 클릭 시 해당 페이지로 이동
    if (notification.notification.data && notification.notification.data.type === 'analysis_complete') {
      const historyId = notification.notification.data.historyId;
      const notificationId = notification.notification.data.notificationId;
      
      if (historyId) {
        console.log(`🚀 [Push] ${platform} 알림 클릭으로 페이지 이동: /analyze/${historyId}`);
        
        // 🔔 개선된 알림 읽음 처리 (비동기, 실패해도 네비게이션은 계속)
        if (notificationId) {
          markNotificationAsRead(notificationId, platform); // await 없이 백그라운드 실행
        }
        
        // 🎯 현재 페이지와 동일한 경우 navigate 하지 않음 (컴포넌트 리렌더링 방지)
        const currentPath = window.location.pathname;
        const targetPath = `/analyze/${historyId}`;
        
        if (currentPath === targetPath) {
          console.log(`📍 [Push] ${platform} 이미 해당 페이지에 있음 - navigate 생략하여 모달 유지`);
        } else {
          console.log(`🚀 [Push] ${platform} 다른 페이지로 이동: ${targetPath}`);
          navigate(targetPath);
        }
      }
    }
  };

  // 🌐 플랫폼별 FCM 처리 (iOS는 기존 방식 유지, Android는 직접 함수 호출 방식)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    const platform = Capacitor.getPlatform();
    console.log(`🔧 [FCM] ${platform} 플랫폼별 처리 시작`);
    
    // iOS는 기존 handleNotificationAction 유지
    window.handleNotificationAction = handleNotificationAction;
    
    // 🤖 Android 전용: React Router navigate 함수 노출
    if (platform === 'android') {
      console.log('🤖 [Android] React Router navigate 함수 노출');
      window.reactRouterNavigate = navigate;
      
      return () => {
        console.log('🤖 [Android] 전역 함수 정리');
        delete window.handleNotificationAction;
        delete window.reactRouterNavigate;
      };
    } else {
      // 🍎 iOS는 기존 방식 그대로 유지
      console.log('🍎 [iOS] 기존 푸시 알림 처리 방식 유지');
      
      return () => {
        delete window.handleNotificationAction;
      };
    }
  }, [handleNotificationAction, navigate]); // 🍎 iOS 호환성을 위해 의존성 배열 복원

  // 로그인 완료 시 대기 중인 FCM 토큰 처리
  useEffect(() => {
    if (userId) {
      console.log('🔍 [FCM] 로그인 완료 - 대기 중인 FCM 토큰 확인');
      
      // 중복 등록 방지 로직 제거 (근본원인 해결로 불필요)
      
      const pendingToken = sessionStorage.getItem('pendingFCMToken');
      if (pendingToken) {
        try {
          const tokenData = JSON.parse(pendingToken);
          console.log('📋 [FCM] 대기 중인 토큰 발견:', tokenData);
          
          // sessionStorage에서 토큰 삭제
          sessionStorage.removeItem('pendingFCMToken');
          
          // 토큰 등록 이벤트 발생
          const event = new CustomEvent('fcmTokenReceived', {
            detail: tokenData
          });
          
          setTimeout(() => {
            console.log('🚀 [FCM] 대기 중인 토큰 등록 시작');
            window.dispatchEvent(event);
          }, 500);
          
        } catch (error) {
          console.error('❌ [FCM] 대기 토큰 파싱 실패:', error);
        }
      }
      
      // 등록 상태 업데이트 로직 제거 (근본원인 해결로 불필요)
    }
  }, [userId]);

  // 앱 활성화 시 뱃지 초기화 및 알림 읽음 처리
  useEffect(() => {
    if (!userId) return;

    const handleAppFocus = async () => {
      if (Capacitor.isNativePlatform()) {
        const platform = Capacitor.getPlatform();
        console.log(`🔄 [Badge] ${platform} 앱 활성화 감지 - 뱃지 초기화 시작`);
        
        try {
          // 1. 온라인 복구 시 대기 중인 알림 읽음 처리
          await processOfflineNotificationQueue();
          
          // 2. 백엔드에서 모든 알림을 읽음으로 처리
          const response = await api.post('/api/v1/fcm/mark-all-notifications-read', null, {
            headers: { 'user-id': userId }
          });
          
          if (response.data.success) {
            console.log(`✅ [Badge] ${platform} 서버 알림 읽음 처리 완료: ${response.data.updated_count}개 알림`);
          }
          
          // 3. iOS의 경우 네이티브 뱃지도 초기화
          if (platform === 'ios') {
            try {
              const { PushNotifications } = await import('@capacitor/push-notifications');
              await PushNotifications.removeAllDeliveredNotifications();
              console.log('🎉 [Badge] iOS 네이티브 뱃지 초기화 완료');
            } catch (error) {
              console.error('❌ [Badge] iOS 뱃지 초기화 실패:', error);
            }
          }
          
        } catch (error) {
          console.error(`❌ [Badge] ${platform} 뱃지 초기화 실패:`, error);
        }
      }
    };

    // 페이지 포커스 이벤트 리스너 등록
    window.addEventListener('focus', handleAppFocus);
    
    // Capacitor의 앱 상태 변화 이벤트도 처리 (모바일 앱 전용)
    if (Capacitor.isNativePlatform()) {
      const handleAppStateChange = async (state) => {
        if (state.isActive) {
          await handleAppFocus();
        }
      };

      // App 플러그인 사용 (필요시)
      import('@capacitor/app').then(({ App }) => {
        App.addListener('appStateChange', handleAppStateChange);
      }).catch(() => {
        console.log('📱 [Badge] App 플러그인 사용 불가, 페이지 포커스 이벤트만 사용');
      });
    }
    
    return () => {
      window.removeEventListener('focus', handleAppFocus);
    };
  }, [userId, api]);

  // 🌐 온라인/오프라인 상태 감지 및 대기열 처리
  useEffect(() => {
    if (!userId) return;

    const handleOnline = async () => {
      console.log('🌐 [Push] 온라인 복구 감지 - 대기 중인 알림 읽음 처리 시작');
      await processOfflineNotificationQueue();
    };

    const handleOffline = () => {
      console.log('📱 [Push] 오프라인 상태 감지 - 알림 읽음 처리 대기열 모드 활성화');
    };

    // 온라인/오프라인 이벤트 리스너 등록
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // 초기 온라인 상태 확인 (페이지 로드 시)
    if (navigator.onLine) {
      setTimeout(() => {
        processOfflineNotificationQueue();
      }, 1000); // 1초 후 대기열 처리
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [userId]);

  // Google Analytics 페이지뷰 추적
  useEffect(() => {
    pageview(window.location.pathname + window.location.search);
  }, [location]);

  // 보호된 라우트 컴포넌트
  const ProtectedRoute = ({ children }) => {
    const navigate = useNavigate();
    const [authChecked, setAuthChecked] = useState(false);
    
    useEffect(() => {
      // userId가 있으면 즉시 인증 완료로 간주
      if (userId) {
        setAuthChecked(true);
        return;
      }
      
      // userId가 없으면 잠시 기다린 후 인증 체크 완료
      const timer = setTimeout(() => {
        setAuthChecked(true);
      }, 1500); // 1.5초 후 인증 체크 완료로 간주

      return () => clearTimeout(timer);
    }, [userId]);

    useEffect(() => {
      // 인증 체크가 완료되고 로그인되지 않은 경우에만 리다이렉트
      if (authChecked && !isLoggedIn) {
        navigate('/');
      }
    }, [authChecked, isLoggedIn, navigate]);

    // 인증 체크 중이거나 로그인된 경우 children 렌더링
    if (!authChecked || isLoggedIn) {
      return children;
    }

    return null;
  };

  // 로그인 완료 후 Share Extension에서 저장된 분석 요청 처리
  useEffect(() => {
    const processPendingAnalysis = async () => {
      if (userId) { // 로그인 완료 감지
        console.log('🔍 [로그인 완료] 대기 중인 Share Extension 분석 요청 확인');
        
        const pendingAnalysisData = sessionStorage.getItem('pendingAnalysis');
        if (pendingAnalysisData) {
          try {
            const data = JSON.parse(pendingAnalysisData);
            console.log('📋 [대기 분석] 발견된 데이터:', data);
            
            // sessionStorage에서 데이터 삭제 (한 번만 실행되도록)
            sessionStorage.removeItem('pendingAnalysis');
            
            // 🌐 대기 분석의 언어로 UI 언어 변경
            if (data.language && data.language !== i18n.language) {
              console.log(`🌐 [대기 분석] UI 언어 변경: ${i18n.language} → ${data.language}`);
              await i18n.changeLanguage(data.language);
              // 언어 설정을 localStorage에 저장 (다음 앱 실행 시에도 유지)
              localStorage.setItem('i18nextLng', data.language);
              console.log(`💾 [대기 분석] 언어 설정 저장 완료: ${data.language}`);
            } else {
              console.log(`📝 [대기 분석] UI 언어 유지: ${i18n.language} (선택한 언어와 동일)`);
            }
            
            // Android와 iOS 데이터 구조 통합
            const videoURL = data.youtubeUrl || data.videoURL; // Android는 videoURL, iOS는 youtubeUrl
            let videoId = data.videoId;
            
            // Android에서는 videoId를 별도로 전송하지 않으므로 URL에서 추출
            if (!videoId && videoURL) {
              try {
                const { extractVideoId } = await import('./utils');
                videoId = extractVideoId(videoURL);
              } catch (error) {
                console.error('❌ [대기 분석] videoId 추출 실패:', error);
              }
            }
            
            const shareData = {
              videoURL: videoURL,
              videoId: videoId,
              videoTitle: data.title || '',
              language: data.language,
              timestamp: data.timestamp,
              source: 'pending-analysis'
            };
            
            const event = new CustomEvent('tubeDigestShareAnalysis', {
              detail: shareData
            });
            
            console.log('🚀 [대기 분석] 자동 분석 시작');
            
            // 약간의 지연 후 이벤트 발생
            setTimeout(() => {
              window.dispatchEvent(event);
            }, 500);
            
          } catch (error) {
            console.error('❌ [대기 분석] 데이터 파싱 실패:', error);
            sessionStorage.removeItem('pendingAnalysis'); // 잘못된 데이터 제거
          }
        } else {
          console.log('📄 [로그인 완료] 대기 중인 분석 요청 없음');
        }
      }
    };
    
    processPendingAnalysis();
  }, [userId]); // userId 변경 시 (로그인 완료 시) 실행

  // ✅ [Android FCM] 앱 아이콘 클릭과 동일한 프로세스
  useEffect(() => {
    if (Capacitor.getPlatform() === 'android') {
      console.log('🚀 [Android FCM] 앱 아이콘 클릭과 동일한 프로세스로 설정');
      console.log('  → useBackgroundReconnect 훅이 자동으로 100% 모달 표시');
      // 복잡한 FCM 처리 없음 - 일반적인 앱 시작과 동일하게 처리
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <APIConnectionTest />
      <ErrorBoundary>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route 
            path="/analyze/:historyId" 
          element={
            <ProtectedRoute>
                <AnalyzePage />
            </ProtectedRoute>
          } 
        />
        <Route path="/auth/naver/callback" element={<NaverCallback />} />
        <Route path="/login/success" element={<GoogleCallback />} />
        <Route path="/login/error" element={<GoogleCallback />} />
        <Route path="/google-callback" element={<GoogleCallback />} />
        <Route path="/kakao-callback" element={<KakaoCallback />} />
        <Route path="/auth/action" element={<EmailActionPage />} />
        <Route path="/auth/verify-email" element={<EmailVerificationPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ErrorBoundary>
      
      {/* 전역 AlertDialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={closeAlert}
        type={alertDialog.type}
        title={alertDialog.title}
        message={alertDialog.message}
        confirmText={alertDialog.confirmText}
      />
      
      {/* Share Extension 로딩 스피너 */}
      <LoadingSpinner 
        open={shareExtensionLoading}
        message={t('common.loading')} 
      />
      
      {/* Share Extension 에러 모달 (기존 ErrorModal 사용) */}
      <ErrorModal
        open={shareErrorModalOpen}
        onClose={closeShareErrorModal}
        errorType={shareErrorType}
        customMessage={shareErrorMessage}
        onRetry={null} // Share Extension에서는 재시도 없음
      />
    </ThemeProvider>
  );
}

export default App;